/**
 * Props for `<sp-avatar>` — a sized, shaped container that renders, in priority
 * order, an image, an `sp-icon` glyph, short text (initials), or the default
 * slot. Modelled on Vuetify's `<v-avatar>` (image / icon / text + size + shape).
 */
import { booleanAttribute } from "#ce/utils/props";

export const props = Object.freeze({
  /**
   * Image URL. Rendered as a cover/contain image filling the avatar. If the
   * image fails to load it falls through to `icon`, then `text`, then the slot.
   *
   * @type {String}
   * @default undefined
   */
  image: {
    type: String,
    default: undefined,
  },
  /**
   * `sp-icon` sprite name, used as the glyph when no `image` is set (or when the
   * image fails to load). The single most common avatar fallback.
   *
   * @type {String}
   * @default undefined
   */
  icon: {
    type: String,
    default: undefined,
  },
  /**
   * Short text (e.g. initials) shown when neither `image` nor `icon` is set.
   *
   * @type {String}
   * @default undefined
   */
  text: {
    type: String,
    default: undefined,
  },
  /**
   * Avatar size as a CSS length. A bare number is treated as rem. Defaults to
   * the `--sp-comp-avatar-size` token when unset.
   *
   * @type {String|Number}
   * @default undefined
   */
  size: {
    type: [String, Number],
    default: undefined,
  },
  /**
   * Container shape: `circle` (default), `rounded`, or `square`.
   *
   * @type {String}
   * @default "circle"
   * @values circle, rounded, square
   * @storybook-control select
   */
  shape: {
    type: String,
    default: "circle",
  },
  /**
   * Background colour behind the image/glyph (applied verbatim). For an image
   * avatar it shows only behind transparent regions; for a glyph it is the
   * circle fill.
   *
   * @type {String}
   * @default undefined
   */
  color: {
    type: String,
    default: undefined,
  },
  /**
   * Glyph fill colour, forwarded to the inner `sp-icon` as `fill-color`.
   *
   * @type {String}
   * @default undefined
   */
  fillColor: {
    type: String,
    default: undefined,
  },
  /**
   * Whether an `image` covers the avatar (`object-fit: cover`, the default) or
   * is contained within it (`object-fit: contain`).
   *
   * @type {Boolean|String}
   * @default true
   */
  cover: booleanAttribute(true),
  /**
   * Alt text for the image.
   *
   * @type {String}
   * @default ""
   */
  alt: {
    type: String,
    default: "",
  },
  /**
   * Accessible name for the avatar as a whole. When set, the avatar is exposed
   * as `role="img"` with this label and its inner glyph/image is hidden from
   * assistive tech (so it announces once). When unset the avatar is treated as
   * decorative — the icon fallback is `aria-hidden` and the image uses `alt` —
   * which is correct when an adjacent element (e.g. a list-item title) already
   * names the person/place.
   *
   * @type {String}
   * @default undefined
   */
  label: {
    type: String,
    default: undefined,
  },
});
